import React, { useState, useEffect } from 'react';
import { goalsAPI, partnerAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { toast } from 'sonner';
import { 
  Target, Trophy, TrendingUp, Users, Bell, Plus, Zap, 
  ArrowUp, IndianRupee, Calendar, Medal, Crown, AlertCircle
} from 'lucide-react';

const TIER_COLORS = {
  bronze: 'bg-orange-100 text-orange-700 border-orange-300',
  silver: 'bg-slate-200 text-slate-700 border-slate-400',
  gold: 'bg-amber-100 text-amber-700 border-amber-400',
  platinum: 'bg-violet-100 text-violet-700 border-violet-400',
};

const TIER_ICONS = {
  bronze: Medal,
  silver: Medal,
  gold: Trophy,
  platinum: Crown,
};

const PerformanceGoalsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [partners, setPartners] = useState([]);
  const [period, setPeriod] = useState('monthly');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newGoal, setNewGoal] = useState({
    partner_id: '',
    period: 'monthly',
    target_leads: '',
    target_bookings: '',
    target_revenue: '',
    target_site_visits: ''
  });

  const isAdmin = ['builder_admin', 'sales_team'].includes(user?.role);

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [progressRes, notificationsRes, partnersRes] = await Promise.all([
        goalsAPI.getProgress(period),
        goalsAPI.getTierNotifications(),
        isAdmin ? partnerAPI.list('approved') : Promise.resolve({ data: [] })
      ]);
      setProgress(progressRes.data);
      setNotifications(notificationsRes.data);
      setPartners(partnersRes.data);
    } catch (error) {
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async () => {
    try {
      const goalData = {
        partner_id: newGoal.partner_id && newGoal.partner_id !== 'all' ? newGoal.partner_id : null,
        period: newGoal.period,
        target_leads: newGoal.target_leads ? parseInt(newGoal.target_leads) : null,
        target_bookings: newGoal.target_bookings ? parseInt(newGoal.target_bookings) : null,
        target_revenue: newGoal.target_revenue ? parseFloat(newGoal.target_revenue) : null,
        target_site_visits: newGoal.target_site_visits ? parseInt(newGoal.target_site_visits) : null
      };
      
      await goalsAPI.create(goalData);
      toast.success('Performance goal created successfully');
      setShowCreateDialog(false);
      setNewGoal({
        partner_id: '',
        period: 'monthly',
        target_leads: '',
        target_bookings: '',
        target_revenue: '',
        target_site_visits: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create goal');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    return `₹${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="performance-goals-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-7 w-7 text-emerald-600" />
            Performance Goals
          </h1>
          <p className="text-muted-foreground">Track partner progress and tier upgrades</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
          
          {isAdmin && (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button data-testid="btn-set-goal">
                  <Plus className="h-4 w-4 mr-2" />
                  Set Goal
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Set Performance Goal</DialogTitle>
                  <DialogDescription>
                    Create targets for partners to achieve
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Apply To</Label>
                    <Select 
                      value={newGoal.partner_id} 
                      onValueChange={(v) => setNewGoal({...newGoal, partner_id: v})}
                    >
                      <SelectTrigger data-testid="goal-partner-select">
                        <SelectValue placeholder="All Partners" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Partners</SelectItem>
                        {partners.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.user?.name || 'Partner'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Period</Label>
                    <Select 
                      value={newGoal.period} 
                      onValueChange={(v) => setNewGoal({...newGoal, period: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Target Leads</Label>
                      <Input 
                        type="number" 
                        placeholder="e.g., 50"
                        value={newGoal.target_leads}
                        onChange={(e) => setNewGoal({...newGoal, target_leads: e.target.value})}
                        data-testid="input-target-leads"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Target Bookings</Label>
                      <Input 
                        type="number" 
                        placeholder="e.g., 5"
                        value={newGoal.target_bookings}
                        onChange={(e) => setNewGoal({...newGoal, target_bookings: e.target.value})}
                        data-testid="input-target-bookings"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Target Revenue (₹)</Label>
                      <Input 
                        type="number" 
                        placeholder="e.g., 50000000"
                        value={newGoal.target_revenue}
                        onChange={(e) => setNewGoal({...newGoal, target_revenue: e.target.value})}
                        data-testid="input-target-revenue"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Target Site Visits</Label>
                      <Input 
                        type="number" 
                        placeholder="e.g., 20"
                        value={newGoal.target_site_visits}
                        onChange={(e) => setNewGoal({...newGoal, target_site_visits: e.target.value})}
                        data-testid="input-target-visits"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateGoal} data-testid="btn-save-goal">
                    Create Goal
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Tier Upgrade Notifications */}
      {notifications.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Bell className="h-5 w-5 text-amber-500" />
            Tier Upgrade Alerts
          </h2>
          <div className="grid gap-3">
            {notifications.map((notif, idx) => {
              const NextIcon = TIER_ICONS[notif.next_tier] || Trophy;
              return (
                <Alert key={idx} className="border-amber-200 bg-amber-50/50">
                  <Zap className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="flex items-center gap-2">
                    {notif.partner_name}
                    <Badge className={TIER_COLORS[notif.current_tier]}>
                      {notif.current_tier}
                    </Badge>
                    <ArrowUp className="h-4 w-4" />
                    <Badge className={TIER_COLORS[notif.next_tier]}>
                      <NextIcon className="h-3 w-3 mr-1" />
                      {notif.next_tier}
                    </Badge>
                  </AlertTitle>
                  <AlertDescription>
                    {notif.message} Commission rate will increase from {notif.current_rate}% to {notif.next_rate}%.
                  </AlertDescription>
                </Alert>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Users className="h-4 w-4" />
              <span className="text-sm">Total Partners</span>
            </div>
            <p className="text-2xl font-bold">{progress.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <Zap className="h-4 w-4" />
              <span className="text-sm">Close to Upgrade</span>
            </div>
            <p className="text-2xl font-bold text-amber-700">{notifications.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <Target className="h-4 w-4" />
              <span className="text-sm">Total Bookings</span>
            </div>
            <p className="text-2xl font-bold">
              {progress.reduce((sum, p) => sum + p.actual.bookings, 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <IndianRupee className="h-4 w-4" />
              <span className="text-sm">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold">
              {formatCurrency(progress.reduce((sum, p) => sum + p.actual.revenue, 0))}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Partner Progress Cards */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Partner Progress</h2>
        
        {progress.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Target className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No partners found</p>
              <p className="text-muted-foreground">Partners will appear here once approved</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {progress.map((partner) => {
              const TierIcon = TIER_ICONS[partner.current_tier] || Medal;
              const tierProgress = partner.tier_progress;
              
              return (
                <Card 
                  key={partner.partner_id} 
                  className={`${tierProgress?.close_to_upgrade ? 'border-amber-300 bg-amber-50/30' : ''}`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      {/* Partner Info */}
                      <div className="flex items-center gap-3 min-w-[200px]">
                        <div className={`h-12 w-12 rounded-full flex items-center justify-center ${TIER_COLORS[partner.current_tier].split(' ')[0]}`}>
                          <TierIcon className={`h-6 w-6 ${TIER_COLORS[partner.current_tier].split(' ')[1]}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold">{partner.partner_name}</h3>
                          <div className="flex items-center gap-2">
                            <Badge className={TIER_COLORS[partner.current_tier]}>
                              {partner.current_tier}
                            </Badge>
                            <span className="text-sm text-emerald-600 font-medium">
                              {partner.commission_rate}%
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Performance Stats */}
                      <div className="flex-1 grid grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-xl font-bold">{partner.actual.leads}</p>
                          <p className="text-xs text-muted-foreground">Leads</p>
                          {partner.goal?.target_leads && (
                            <p className="text-xs text-blue-600">
                              /{partner.goal.target_leads}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-xl font-bold">{partner.actual.site_visits}</p>
                          <p className="text-xs text-muted-foreground">Visits</p>
                          {partner.goal?.target_site_visits && (
                            <p className="text-xs text-blue-600">
                              /{partner.goal.target_site_visits}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-xl font-bold text-emerald-600">{partner.actual.bookings}</p>
                          <p className="text-xs text-muted-foreground">Bookings</p>
                          {partner.goal?.target_bookings && (
                            <p className="text-xs text-blue-600">
                              /{partner.goal.target_bookings}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-xl font-bold">{formatCurrency(partner.actual.revenue)}</p>
                          <p className="text-xs text-muted-foreground">Revenue</p>
                          {partner.goal?.target_revenue && (
                            <p className="text-xs text-blue-600">
                              /{formatCurrency(partner.goal.target_revenue)}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Tier Progress */}
                      {tierProgress && (
                        <div className="lg:w-64 space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                              {tierProgress.bookings_needed} to {tierProgress.next_tier}
                            </span>
                            <span className="text-emerald-600 font-medium">
                              <ArrowUp className="h-3 w-3 inline" /> {tierProgress.next_rate}%
                            </span>
                          </div>
                          <Progress 
                            value={tierProgress.progress_percentage} 
                            className={`h-2 ${tierProgress.close_to_upgrade ? 'bg-amber-100' : ''}`}
                          />
                          {tierProgress.close_to_upgrade && (
                            <p className="text-xs text-amber-600 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Almost there!
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Goal Progress Bars */}
                    {partner.progress_percentage && (
                      <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
                        {partner.progress_percentage.leads !== null && (
                          <div>
                            <div className="flex justify-between text-xs mb-1">
                              <span>Leads Goal</span>
                              <span className="font-medium">{Math.min(partner.progress_percentage.leads, 100).toFixed(0)}%</span>
                            </div>
                            <Progress value={Math.min(partner.progress_percentage.leads, 100)} className="h-1.5" />
                          </div>
                        )}
                        {partner.progress_percentage.bookings !== null && (
                          <div>
                            <div className="flex justify-between text-xs mb-1">
                              <span>Bookings Goal</span>
                              <span className="font-medium">{Math.min(partner.progress_percentage.bookings, 100).toFixed(0)}%</span>
                            </div>
                            <Progress value={Math.min(partner.progress_percentage.bookings, 100)} className="h-1.5" />
                          </div>
                        )}
                        {partner.progress_percentage.revenue !== null && (
                          <div>
                            <div className="flex justify-between text-xs mb-1">
                              <span>Revenue Goal</span>
                              <span className="font-medium">{Math.min(partner.progress_percentage.revenue, 100).toFixed(0)}%</span>
                            </div>
                            <Progress value={Math.min(partner.progress_percentage.revenue, 100)} className="h-1.5" />
                          </div>
                        )}
                        {partner.progress_percentage.site_visits !== null && (
                          <div>
                            <div className="flex justify-between text-xs mb-1">
                              <span>Visits Goal</span>
                              <span className="font-medium">{Math.min(partner.progress_percentage.site_visits, 100).toFixed(0)}%</span>
                            </div>
                            <Progress value={Math.min(partner.progress_percentage.site_visits, 100)} className="h-1.5" />
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceGoalsPage;
